import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

export type Coords = { lat: number; lng: number };

type CacheState =
  | { status: 'idle' }
  | { status: 'pending'; promise: Promise<Coords | null> }
  | { status: 'resolved'; coords: Coords }
  | { status: 'denied' };

let cache: CacheState = { status: 'idle' };
let requestCount = 0;

const DEBUG_GEO =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV) ||
  Capacitor.isNativePlatform();

function log(...args: unknown[]) {
  if (DEBUG_GEO) console.info('[geo]', ...args);
}

export function getCachedCoords(): Coords | null {
  return cache.status === 'resolved' ? cache.coords : null;
}

export function isLocationDenied(): boolean {
  return cache.status === 'denied';
}

export function resetGeolocationCache() {
  cache = { status: 'idle' };
  requestCount = 0;
}

// Expose a tiny debug surface on window so we can verify from Safari Web
// Inspector / Xcode that the platform prompt fires at most once per launch.
if (DEBUG_GEO && typeof window !== 'undefined') {
  (window as any).__brrgrGeo = {
    get requestCount() {
      return requestCount;
    },
    get cacheState() {
      return cache.status;
    },
    reset: () => resetGeolocationCache(),
  };
}

/**
 * Request the device location, prompting the user at most once per app
 * launch. On native platforms we use Capacitor's Geolocation plugin
 * exclusively to avoid a second WKWebView permission prompt that would
 * otherwise be triggered by `navigator.geolocation`.
 *
 * Returns `null` if permission was denied or location is unavailable.
 */
export function getDeviceLocation(opts?: {
  enableHighAccuracy?: boolean;
  timeout?: number;
}): Promise<Coords | null> {
  if (cache.status === 'resolved') {
    log('cache hit (resolved)');
    return Promise.resolve(cache.coords);
  }
  if (cache.status === 'denied') {
    log('cache hit (denied)');
    return Promise.resolve(null);
  }
  if (cache.status === 'pending') {
    log('cache hit (pending)');
    return cache.promise;
  }

  const enableHighAccuracy = opts?.enableHighAccuracy ?? true;
  const timeout = opts?.timeout ?? 15000;
  const platform = Capacitor.isNativePlatform() ? 'native' : 'web';

  const promise = (async (): Promise<Coords | null> => {
    try {
      requestCount += 1;
      log(`requesting platform location (${platform}), count=${requestCount}`);

      if (Capacitor.isNativePlatform()) {
        // Native: use Capacitor only. Never call navigator.geolocation here,
        // otherwise the WKWebView shows its own separate permission prompt.
        try {
          const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy, timeout });
          const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          cache = { status: 'resolved', coords };
          log('resolved');
          return coords;
        } catch {
          const perm = await Geolocation.requestPermissions();
          if (perm.location === 'denied' && perm.coarseLocation === 'denied') {
            cache = { status: 'denied' };
            log('denied');
            return null;
          }
          const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy, timeout });
          const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          cache = { status: 'resolved', coords };
          log('resolved (after permission grant)');
          return coords;
        }
      }

      // Web: use the browser API.
      if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
        cache = { status: 'denied' };
        log('denied (no navigator.geolocation)');
        return null;
      }
      const coords = await new Promise<Coords | null>((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
          () => resolve(null),
          { enableHighAccuracy, timeout, maximumAge: 300000 },
        );
      });
      if (!coords) {
        cache = { status: 'denied' };
        log('denied');
        return null;
      }
      cache = { status: 'resolved', coords };
      log('resolved');
      return coords;
    } catch {
      cache = { status: 'denied' };
      log('denied (exception)');
      return null;
    }
  })();

  cache = { status: 'pending', promise };
  return promise;
}
